// utils
import EmailSignatureImagesUploader from '@components/EmailSignaturev2/EmailSignatureEdit/EmailSignatureImagesUploader';
// components
import { OnboardingEmailSignatureEditor } from '@components/EmailSignaturev2/OnboardingFlow/desktop/OnboardingEmailSignatureEditor';
import OnboardingDetailsPageForMobile from '@components/EmailSignaturev2/OnboardingFlow/mobile/details';
import {
  OnboardingFlowContainerForDesktop,
  OnboardingFlowContainerForMobile,
} from '@components/EmailSignaturev2/OnboardingFlow/OnboardingFlowContainers';
import { ImagesUploadingErrorMessage } from '@components/EmailSignaturev2/OnboardingFlow/OnboardingMessages';
import useOnboardingSignature from '@components/EmailSignaturev2/OnboardingFlow/useOnboardingSignature';
import {
  createNewEmailSignature,
  createNewUserFromEmailSignature,
  getMemorisedOnboardingEmailSignatureId,
  memoriseOnboardingEmailSignatureId,
  updateEmailSignature,
  updateUserFromEmailSignature,
} from '@components/EmailSignaturev2/OnboardingFlow/utils';
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
import BlinqTopNav from '@components/TopNav/BlinqTopNav';
import { WithAnonymousUser } from '@components/WithUser/WithUser';
import { useUser } from '@context/user-context';
// HOC and hooks
import useAnonymousUser from '@hooks/use-anonymous-user';
import type { NextPage } from 'next';
import { useCallback, useRef, useState } from 'react';
import type { User } from 'types';

const EmailSignatureOnboardingDetailsPage: NextPage = () => {
  const { user } = useUser();
  const anonymousUser = useAnonymousUser();
  const emailSignatureImagesUploader = useRef(new EmailSignatureImagesUploader()).current;
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const getOnboardingEmailSignatureId = useCallback((userId: string) => {
    return getMemorisedOnboardingEmailSignatureId(userId);
  }, []);

  const [
    onboardingEmailSignature,
    onboardingEmailSignatureDispatch,
    onboardingEmailSignatureHasLoaded,
    isEditingSavedEmailSignature,
  ] = useOnboardingSignature(getOnboardingEmailSignatureId);

  const uploadEmailSignatureFieldImagesThunk = useCallback(
    (uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void) =>
      uploadImage(emailSignatureImagesUploader),
    [emailSignatureImagesUploader],
  );

  const handleSave = useCallback(
    async (
      emailSignature: EmailSignatureV2,
      onComplete: (savedEmailSignature: EmailSignatureV2, savedUser: User) => void,
    ): Promise<void> => {
      if (!anonymousUser?.uid) {
        return;
      }

      try {
        await emailSignatureImagesUploader.finishUpAllUploadingJobs();
        const { emailSignatureWithStorageImagesUrls } =
          emailSignatureImagesUploader.replaceLocalImagesUrlsWithStorageImagesUrls(emailSignature);

        const userObjectHasNotBeenCreated = !user?.userId;

        const userSavePromise = userObjectHasNotBeenCreated
          ? createNewUserFromEmailSignature(anonymousUser, emailSignatureWithStorageImagesUrls)
          : updateUserFromEmailSignature(user, emailSignatureWithStorageImagesUrls);
        const emailSignatureSavePromise = emailSignatureWithStorageImagesUrls.id
          ? updateEmailSignature(emailSignatureWithStorageImagesUrls)
          : createNewEmailSignature(anonymousUser, emailSignatureWithStorageImagesUrls);

        const [savedEmailSignature, savedUser] = await Promise.all([
          emailSignatureSavePromise,
          userSavePromise,
        ]);
        memoriseOnboardingEmailSignatureId(savedEmailSignature.userId, savedEmailSignature.id);
        emailSignatureImagesUploader.clearEverything();
        onComplete(savedEmailSignature, savedUser);
      } catch {
        setShowErrorMessage(true);
      }
    },
    [anonymousUser, emailSignatureImagesUploader, user],
  );

  return (
    <>
      <OnboardingFlowContainerForDesktop isDisplaying={!showErrorMessage}>
        <BlinqTopNav />
        <OnboardingEmailSignatureEditor
          handleSave={handleSave}
          uploadEmailSignatureFieldImagesThunk={uploadEmailSignatureFieldImagesThunk}
          onboardingEmailSignature={onboardingEmailSignature}
          onboardingEmailSignatureDispatch={onboardingEmailSignatureDispatch}
          onboardingEmailSignatureHasLoaded={onboardingEmailSignatureHasLoaded}
          isEditingSavedEmailSignature={isEditingSavedEmailSignature}
        />
      </OnboardingFlowContainerForDesktop>

      <OnboardingFlowContainerForMobile isDisplaying={!showErrorMessage}>
        <OnboardingDetailsPageForMobile
          handleSave={handleSave}
          uploadEmailSignatureFieldImagesThunk={uploadEmailSignatureFieldImagesThunk}
          onboardingEmailSignature={onboardingEmailSignature}
          onboardingEmailSignatureHasLoaded={onboardingEmailSignatureHasLoaded}
          onboardingEmailSignatureDispatch={onboardingEmailSignatureDispatch}
        />
      </OnboardingFlowContainerForMobile>
      {showErrorMessage && <ImagesUploadingErrorMessage />}
    </>
  );
};

export default WithAnonymousUser(EmailSignatureOnboardingDetailsPage);
